<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <v-card-title>
        Organizations
        <v-spacer />
        <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details />
        <v-btn v-if="canCreate" class="ml-4" color="primary" depressed @click="openAdd">
          <v-icon left>mdi-plus-circle</v-icon>Add Organization
        </v-btn>
      </v-card-title>
      <v-data-table
          :headers="headers"
          :items="organizations.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="organizations.count"
          class="elevation-1"
      >
        <template v-if="canEdit" v-slot:[`item.actions`]="{ item }">
          <v-icon class="mr-3" @click="openEdit(item)">mdi-circle-edit-outline</v-icon>
          <v-icon color="red" @click="remove(item)">mdi-delete-outline</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent width="50%">
      <v-card>
        <v-card-title class="headline font-weight-regular blue-grey white--text">
          {{ title }}
          <v-spacer />
          <v-btn color="white" text @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <AddOrganization
            v-if="dialog"
            :organization="organization"
            @close="dialog = false"
            @save="save"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {getUser} from "@/utils/helper";
import {LookupService} from "@/common/api.service";
import AddOrganization from "./add-organization.vue";
import {OrganizationService} from "./organization.service";

export default {
  components: {AddOrganization},
  data() {
    return {
      search: "",
      searchDebounce: null,
      loading: false,
      organizations: {rows: [], count: 0},
      headers: [
        {text: "Organization Name", value: "name"},
        {text: "Organization Type", value: "type"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      options: {page: 1, itemsPerPage: 10},
      dialog: false,
      organization: null,
      title: "",
    };
  },
  computed: {
    canCreate() {
      return getUser().permissions.some((p) => p.menuItem.menuItemName === "Organization" && p.canCreate);
    },
    canEdit() {
      return getUser().permissions.some((p) => p.menuItem.menuItemName === "Organization" && p.canUpdate);
    },
  },
  watch: {
    options: {handler() { this.fetchOrganizations(); }, deep: true},
    search(value) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.fetchOrganizations(), value && value.length < 3 ? 700 : 350);
    },
  },
  methods: {
    async fetchOrganizations() {
      this.loading = true;
      try {
        const response = await LookupService.getOrganizationsWP(
            this.options.page,
            this.options.itemsPerPage,
            this.search
        );
        this.organizations = response.data;
      } catch (error) {
        this.$toast.error("Unable to load organizations.");
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.organization = null;
      this.title = "Add Organization";
      this.dialog = true;
    },
    openEdit(item) {
      this.organization = {...item};
      this.title = "Edit Organization";
      this.dialog = true;
    },
    async save(value) {
      this.loading = true;
      try {
        const response = value.editMode
            ? await OrganizationService.update(value.id, value)
            : await OrganizationService.create(value);
        if (response.data.status === 200) {
          this.$toast.success(response.data.message);
          this.dialog = false;
          await this.fetchOrganizations();
        } else {
          this.$toast.error(response.data.message || "Unable to save organization.");
        }
      } finally {
        this.loading = false;
      }
    },
    async remove(item) {
      if (!window.confirm(`Delete ${item.name}?`)) return;
      const response = await OrganizationService.delete(item.id);
      if (response.data.resultCode === 1) {
        this.$toast.success(response.data.message);
        await this.fetchOrganizations();
      } else {
        this.$toast.error(response.data.message || "Unable to delete organization.");
      }
    },
  },
};
</script>

<style scoped>
.lookup-page { padding: 24px; }
.lookup-card { border-radius: 20px; padding: 8px 8px 16px; }
</style>
